// Browser OpenTelemetry SDK.
//
// Until this existed the SPA minted W3C trace ids and shipped no spans, so
// every backend server span carried a parent_span_id referencing a span that
// was never exported. In SigNoz those render as traces with no root: the
// waterfall starts at the first Go hop and all client-side time — render,
// queue, connection setup, network — is invisible. Confirmed live: every
// server span in staging had a dangling parent.
//
// Spans are posted to /api/otlp/v1/traces, which Caddy already proxies to
// Traefik (see apps/iplant/Caddyfile), so this is same-origin: no CORS, no new
// public hostname. That route is auth-gated and rate-limited on the Traefik
// side — see deploy/helm/cte/templates/otel-ingest-ingressroute.yaml.
//
// In local development the same relative path goes somewhere else: the vite
// proxy answers it and forwards the spans to Loggerhead, the local telemetry
// sink (see apps/iplant/vite.config.ts). The compose collector therefore
// publishes no OTLP port and carries no Traefik route.

import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
} from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";
import { trace } from "@opentelemetry/api";
import { getJwtToken } from "@/lib/auth-client";

const SERVICE_NAME = "iplant";

// The ingest route sits behind the same auth-forward middleware as every
// other /api route, and auth-svc accepts only a Bearer JWT (never the session
// cookie). The SDK reads this factory on every export, so the header follows
// the token the session handshake last minted. Before sign-in there is no
// token and the post gets a 401, which the batch processor drops.
async function authHeaders(): Promise<Record<string, string>> {
  const token = getJwtToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Sampling ratio.
//
// The SPA is built once and promoted across environments — the Dockerfile
// deliberately bakes in no per-environment VITE_* args and no URLs — so
// there is no per-environment value to read here. Deployed builds therefore use the production
// ratio, which is the conservative choice: staging is configured for 0.5, so
// 0.05 merely samples less than it could, whereas the reverse would multiply
// production ingest twentyfold.
//
// This is also what stops the old bug recurring in a new form. The hand-rolled
// header hardcoded the sampled flag to "01", which forced *every* browser-
// originated request through the backends' ParentBased samplers at 100% and
// bypassed the configured ratio entirely — likely the single largest trace
// volume item in the platform.
const SAMPLE_RATIO = import.meta.env.DEV ? 1.0 : 0.05;

// deployment.environment.name, read from the page's hostname.
//
// The one image serves every environment (see the sampling note above), so
// the value cannot be baked in at build time. The names match the backends'
// ENVIRONMENT values: "docker" for the local compose stack (the Vite dev
// server), and global.environment from the Helm values files elsewhere.
function deploymentEnvironment(): string {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host === "::1") {
    return "docker";
  }
  return host.includes("staging") ? "staging" : "production";
}

// service.version. The image build sets VITE_SERVICE_VERSION to the CI run
// number, which is the image tag that is deployed (apps/iplant/Dockerfile).
// Local builds fall back to "dev", as the backends do.
const SERVICE_VERSION = import.meta.env.VITE_SERVICE_VERSION || "dev";

let provider: WebTracerProvider | undefined;

export function initTelemetry(): void {
  if (provider) return;

  provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: SERVICE_NAME,
      [ATTR_SERVICE_VERSION]: SERVICE_VERSION,
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: deploymentEnvironment(),
    }),
    sampler: new TraceIdRatioBasedSampler(SAMPLE_RATIO),
    spanProcessors: [
      new BatchSpanProcessor(
        // Keep this exporter at 0.216 or later. Older versions always send
        // with fetch keepalive, and Chrome rejects a keepalive body over
        // 64 KiB. A bulk upload makes about 185 spans in one batch (74 KB), so
        // the older exporter lost every span of the upload.
        new OTLPTraceExporter({
          url: "/api/otlp/v1/traces",
          headers: authHeaders,
        }),
      ),
    ],
  });

  // Deliberately not registered as the global context manager: the app threads
  // span context explicitly through call sites (see lib/trace.ts) rather than
  // relying on zone/async context, and a ZoneContextManager would pull in
  // zone.js for no benefit here.
  trace.setGlobalTracerProvider(provider);
}

export function getTracer() {
  return trace.getTracer(SERVICE_NAME);
}

// Flush on page hide so spans from the last interaction are not lost when the
// tab closes — BatchSpanProcessor would otherwise drop whatever is still
// buffered. visibilitychange is the reliable signal; unload is not fired on
// mobile Safari.
export function registerTelemetryFlush(): void {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void provider?.forceFlush();
    }
  });
}
